import { Fragment, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import {
  cancelOrderLine,
  deleteOrder,
  cancelPlanUnits
} from '../../features/orders/ordersApi';

/**
 * Cancelling a line, and deleting the order.
 *
 * Both do the same thing at different scope, and it is worth saying plainly on
 * screen because it is not obvious: the cable that was made or promised for this
 * order does NOT disappear. Every product allocated to it gives up that claim,
 * which is exactly what puts its metres back in stock — free to be promised to
 * somebody else — and the warehouse releases any drums it was holding.
 *
 * Nothing is produced twice and nothing is thrown away. That is the whole point,
 * and an operator who does not believe it will not press the button.
 */

const fmt = n => Number(n || 0).toLocaleString();

/** "2 × 1,000 m wood Ø800" — the same wording the order table uses. */
const describeItem = item => {
  const kind = String(item?.kind || '').toUpperCase();
  const what =
    kind === 'COIL'
      ? 'coil'
      : [item?.drumMaterial, item?.drumDiameter ? `Ø${item.drumDiameter}` : '']
          .filter(Boolean)
          .join(' ') || 'drum';
  return `${fmt(item?.qtyPlanned)} × ${fmt(item?.lengthPerUnit)} m ${what}`;
};

/** Units that may still be cancelled: produced ones physically exist. */
const cancellable = item =>
  Math.max(0, Number(item?.qtyPlanned || 0) - Number(item?.qtyProduced || 0));

const OrderLifecycleCard = ({ order }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pending, setPending] = useState(null); // { kind: 'line'|'order', line? }
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [qtyById, setQtyById] = useState({});

  const orderId = order?.orderId || order?._id;
  const lines = Array.isArray(order?.lines) ? order.lines : [];

  const close = () => {
    setPending(null);
    setReason('');
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['factory-events'] });
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data =
        pending.kind === 'units'
          ? await cancelPlanUnits(
              orderId,
              pending.line.lineId,
              pending.item.itemId,
              pending.qty,
              reason.trim() || null
            )
          : pending.kind === 'line'
            ? await cancelOrderLine(orderId, pending.line.lineId, reason.trim() || null)
            : await deleteOrder(orderId, reason.trim() || null);

      close();
      refresh();

      if (pending.kind === 'order') {
        // The order no longer exists; staying on its page would show a 404
        navigate('/apps/sales-management/order-list-view');
        return;
      }
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Request failed');
      close();
    } finally {
      setBusy(false);
    }
  };

  const isCancelled = line => String(line?.status || '').toUpperCase() === 'CANCELLED';
  const liveLines = lines.filter(l => !isCancelled(l));

  return (
    <>
      <Card className="h-100">
        <Card.Body>
          <h3 className="text-1100 mb-1">Lines &amp; cancellation</h3>
          <p className="text-700 fs-9 mb-3">
            Cancelling releases what was promised — the cable goes back to stock and can
            be sold to somebody else. Nothing produced is lost.
          </p>

          {error && (
            <Alert variant="danger" className="py-2">
              {error}
            </Alert>
          )}

          {result && (
            <Alert variant="success" className="py-2">
              {result.cancelledUnits
                ? `${result.cancelledUnits} unit(s) cancelled — ${fmt(result.cancelledMeters)} m off the order`
                : 'Line cancelled'}
              {result.freedMeters ? `, ${fmt(result.freedMeters)} m returned to stock` : ''}
              {result.freedJobs?.length
                ? `, ${result.freedJobs.length} product(s) freed`
                : ''}
              {result.warehouse?.freed
                ? `, ${result.warehouse.freed} drum(s) released in the warehouse`
                : ''}
              .
              {result.warehouse?.error && (
                <div className="fs-9 mt-1">
                  Warehouse could not be reached: {result.warehouse.error} — check the yard.
                </div>
              )}
            </Alert>
          )}

          <div className="table-responsive">
            <Table size="sm" className="fs-9 mb-0">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-end">Ordered</th>
                  <th className="text-end">Allocated</th>
                  <th className="text-end">Produced</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map(line => {
                  const items = line?.deliveryPlan?.items || [];
                  return (
                    <Fragment key={line.lineId}>
                      <tr className={isCancelled(line) ? 'opacity-50' : ''}>
                        <td className="fw-semibold">
                          {line?.product?.cableName || line?.productKey || line.lineId}
                          {isCancelled(line) && (
                            <Badge bg="secondary" variant="phoenix" className="ms-2">
                              cancelled
                            </Badge>
                          )}
                        </td>
                        <td className="text-end">{fmt(line.totalOrderedLength)} m</td>
                        <td className="text-end">{fmt(line.allocatedLength)} m</td>
                        <td className="text-end">{fmt(line.producedLength)} m</td>
                        <td className="text-end">
                          {!isCancelled(line) && (
                            <Button
                              variant="phoenix-warning"
                              size="sm"
                              onClick={() => {
                                setResult(null);
                                setError(null);
                                setReason('');
                                setPending({ kind: 'line', line });
                              }}
                            >
                              Cancel line
                            </Button>
                          )}
                        </td>
                      </tr>

                      {/* The delivery plan, one row per kind of package. A
                          customer trims forty coils or one drum — not the line. */}
                      {!isCancelled(line) &&
                        items.map(item => {
                          const max = cancellable(item);
                          const key = `${line.lineId}:${item.itemId}`;
                          const value = qtyById[key] ?? '';
                          const qty = Number(value);
                          const valid = Number.isFinite(qty) && qty >= 1 && qty <= max;

                          return (
                            <tr key={key}>
                              <td className="ps-4">
                                <Badge
                                  bg={
                                    String(item.kind).toUpperCase() === 'COIL'
                                      ? 'success'
                                      : 'info'
                                  }
                                  variant="phoenix"
                                  className="me-2"
                                >
                                  {String(item.kind || 'DRUM').toUpperCase()}
                                </Badge>
                                {describeItem(item)}
                              </td>
                              <td className="text-end">
                                {fmt(Number(item.qtyPlanned) * Number(item.lengthPerUnit))} m
                              </td>
                              <td className="text-end">
                                {item.qtyProduced ? (
                                  <span className="text-success fw-semibold">
                                    {fmt(item.qtyProduced)} produced
                                  </span>
                                ) : (
                                  <span className="text-body-tertiary">not started</span>
                                )}
                              </td>
                              <td className="text-end">
                                {max > 0 ? (
                                  <span className="text-warning fw-semibold">
                                    {fmt(max)} cancellable
                                  </span>
                                ) : (
                                  <Badge bg="success" variant="phoenix">
                                    all produced
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {max > 0 && (
                                  <div className="d-flex justify-content-end align-items-center gap-2">
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={1}
                                      max={max}
                                      placeholder="qty"
                                      style={{ width: 78 }}
                                      value={value}
                                      onChange={e =>
                                        setQtyById(prev => ({ ...prev, [key]: e.target.value }))
                                      }
                                    />
                                    <Button
                                      variant={valid ? 'warning' : 'phoenix-warning'}
                                      size="sm"
                                      disabled={!valid}
                                      onClick={() => {
                                        setResult(null);
                                        setError(null);
                                        setReason('');
                                        setPending({ kind: 'units', line, item, qty });
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => {
                setResult(null);
                setError(null);
                setReason('');
                setPending({ kind: 'order' });
              }}
            >
              Delete order
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={!!pending} onHide={close} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center gap-2 fs-7">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning" />
            {pending?.kind === 'order'
              ? 'Delete this order'
              : pending?.kind === 'units'
                ? 'Cancel part of the delivery'
                : 'Cancel this line'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {pending?.kind === 'order' ? (
            <>
              <p className="text-700 mb-2">
                {order?.orderCode || orderId}
                {order?.client ? ` — ${order.client}` : ''}, {liveLines.length} active
                line(s).
              </p>
              <p className="text-700 mb-3">
                The order is archived and can be found under Deleted orders. Every
                product launched for it keeps its cable and gives up the claim, so those
                metres return to stock; any drums the warehouse was holding are released.
              </p>
            </>
          ) : pending?.kind === 'units' ? (
            <p className="text-700 mb-3">
              {pending.qty} of {fmt(cancellable(pending.item))} ×{' '}
              {fmt(pending.item?.lengthPerUnit)} m ={' '}
              <b>{fmt(pending.qty * Number(pending.item?.lengthPerUnit || 0))} m</b> comes
              off this order. Anything production had already promised above the new
              total is handed back to stock; units already produced are not touched.
            </p>
          ) : (
            <p className="text-700 mb-3">
              {pending?.line?.product?.cableName || pending?.line?.lineId} —{' '}
              {fmt(pending?.line?.allocatedLength)} m allocated,{' '}
              {fmt(pending?.line?.producedLength)} m already produced. Those metres go
              back to stock and can be reserved for another order.
            </p>
          )}

          <Form.Label className="fs-9 text-700">Reason (optional, kept in the history)</Form.Label>
          <Form.Control
            autoFocus
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="customer cancelled, wrong specification…"
          />
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button variant="phoenix-secondary" onClick={close} disabled={busy}>
            Keep it
          </Button>
          <Button variant="danger" onClick={run} disabled={busy}>
            {busy ? (
              <Spinner animation="border" size="sm" />
            ) : pending?.kind === 'order' ? (
              'Delete order'
            ) : pending?.kind === 'units' ? (
              'Cancel units'
            ) : (
              'Cancel line'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrderLifecycleCard;
